import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { useLocation } from "react-router-dom";

/**
 * SPEC-23 — "I'm operating in another user's data" indicator state.
 *
 * The AdminPage navigates to a borrowed engagement with
 * `location.state.adminView` + `ownerEmail`. We promote that one-shot
 * navigation state into context here so the banner survives navigation
 * between the borrowed engagement's tabs (each tab change is a new
 * location, and React Router drops `state` after the first match).
 *
 * The context auto-clears when the user returns to `/` or `/admin` —
 * those are the two "you're back in your own world" landings.
 */
interface AdminViewState {
  adminView: boolean;
  ownerEmail: string | null;
}

interface AdminViewContextValue extends AdminViewState {
  clear: () => void;
}

const AdminViewContext = createContext<AdminViewContextValue | undefined>(
  undefined,
);

const EMPTY_STATE: AdminViewState = { adminView: false, ownerEmail: null };

export function AdminViewProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const [state, setState] = useState<AdminViewState>(EMPTY_STATE);

  // Promote `location.state.adminView` into context the first time we
  // see it; ignore the same state on subsequent renders so future tab
  // navigations (without the state) don't immediately clear it.
  useEffect(() => {
    const navState = location.state as
      | { adminView?: boolean; ownerEmail?: string | null }
      | null
      | undefined;
    if (navState?.adminView) {
      setState({
        adminView: true,
        ownerEmail: navState.ownerEmail ?? null,
      });
    }
  }, [location.state]);

  // Auto-clear once the admin lands back at one of the "home" routes.
  useEffect(() => {
    if (location.pathname === "/" || location.pathname === "/admin") {
      setState((prev) => (prev.adminView ? EMPTY_STATE : prev));
    }
  }, [location.pathname]);

  const clear = useCallback(() => setState(EMPTY_STATE), []);

  return (
    <AdminViewContext.Provider value={{ ...state, clear }}>
      {children}
    </AdminViewContext.Provider>
  );
}

export function useAdminView(): AdminViewContextValue {
  const ctx = useContext(AdminViewContext);
  if (!ctx) {
    throw new Error("useAdminView must be used within <AdminViewProvider>");
  }
  return ctx;
}
